'use client'

/**
 * PageInfoPanel — SubNavbar instrument readout + page actions.
 *
 * Status: connection · US equities open/closed · US Eastern time (ET).
 * Stock hours and the clock share useUsEquitySession, the same source as the
 * chat panel header, so the two never disagree. The clock is always
 * America/New_York, whatever the browser time zone.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Tooltip } from 'antd'
import {
  ClockCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useTheme } from '@/shared/theme'
import { usePageInfo } from '@/shared/providers/context/PageContext'
import { useLoading } from '@/shared/providers/context/LoadingContext'
import { useConnectionStatus } from '@/infrastructure/api'
import { useUsEquitySession } from '@/shared/utils/market/useUsEquitySession'
import {
  formatEasternClock,
  US_EASTERN_LABEL,
  US_EASTERN_TIME_ZONE,
} from '@/shared/utils/market/easternClock'
import chrome from './AppChrome.module.css'

export function PageInfoPanel() {
  useTheme() // keep theme subscription for dark-mode CSS vars on chrome
  const { pageInfo, isLoading } = usePageInfo()
  const { loadingState } = useLoading()
  const { connectionStatus } = useConnectionStatus()
  const { now: currentTime, session: equitySession } = useUsEquitySession()
  const [isMobile, setIsMobile] = useState(false)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const connectionMeta = useMemo(() => {
    switch (connectionStatus) {
      case 'connected':
        return {
          // r17: this tracks the backend connection, not the market-data stream or the
          // trading mode, so it never says LIVE.
          label: 'Connected',
          title: 'Backend connected',
          tone: 'ok' as const,
        }
      case 'connecting':
        return {
          label: 'Connecting',
          title: 'Backend connecting…',
          tone: 'warn' as const,
        }
      case 'error':
        return {
          label: 'Error',
          title: 'Backend connection error',
          tone: 'error' as const,
        }
      default:
        return {
          label: 'Offline',
          title: 'Backend disconnected',
          tone: 'muted' as const,
        }
    }
  }, [connectionStatus])

  const formatTime = useCallback(
    (date: Date) => formatEasternClock(date, { seconds: !isMobile }),
    [isMobile]
  )

  const showRefresh = Boolean(pageInfo.showRefresh && pageInfo.onRefresh)
  const showSettings = Boolean(pageInfo.showSettings && pageInfo.onSettings)
  const refreshing = Boolean(
    isLoading || loadingState.isRefreshing || loadingState.isAutoRefreshing
  )

  const handleRefresh = useCallback(() => {
    if (refreshing) return
    pageInfo.onRefresh?.()
  }, [pageInfo, refreshing])

  const handleSettings = useCallback(() => {
    pageInfo.onSettings?.()
  }, [pageInfo])

  const timeLabel =
    isClient && currentTime
      ? formatTime(currentTime)
      : isMobile
        ? '--:--'
        : '--:--:--'

  const toneClass =
    connectionMeta.tone === 'ok'
      ? chrome.statusOk
      : connectionMeta.tone === 'warn'
        ? chrome.statusWarn
        : connectionMeta.tone === 'error'
          ? chrome.statusError
          : chrome.statusMuted

  return (
    <div className={chrome.statusPanel} role="group" aria-label="Page status">
      <div className={chrome.statusReadout} role="status">
        <Tooltip title={connectionMeta.title}>
          <span className={`${chrome.statusItem} ${chrome.statusConn} ${toneClass}`} data-conn-status={connectionStatus}>
            <span
              className={chrome.statusMark}
              aria-hidden
              style={connectionStatus === 'connecting' ? { animation: 'none' } : undefined}
            />
            <span>{connectionMeta.label}</span>
          </span>
        </Tooltip>

        <span className={chrome.statusSep} aria-hidden>
          ·
        </span>
        <Tooltip title={equitySession?.title ?? 'US equities regular session'}>
          <span
            className={`${chrome.statusItem} ${
              equitySession?.isOpen ? chrome.statusOk : chrome.statusClosed
            }`}
            data-testid="desk-market-status"
          >
            <span className={chrome.statusMark} aria-hidden />
            {equitySession?.label ?? 'US session'}
          </span>
        </Tooltip>

        {!isMobile && (
          <>
            <span className={chrome.statusSep} aria-hidden>
              ·
            </span>
            <Tooltip title={`US Eastern time (${US_EASTERN_TIME_ZONE})`}>
              <span
                className={`${chrome.statusItem} ${chrome.statusMuted} ${chrome.statusTime}`}
                data-testid="desk-clock"
              >
                <ClockCircleOutlined aria-hidden className={chrome.statusIcon} />
                <span>{`${timeLabel} ${US_EASTERN_LABEL}`}</span>
              </span>
            </Tooltip>
          </>
        )}

        {pageInfo.badge != null &&
          pageInfo.badge !== '' &&
          pageInfo.badge !== 0 && (
            <>
              <span className={chrome.statusSep} aria-hidden>
                ·
              </span>
              <Tooltip title={`${pageInfo.title}: ${pageInfo.badge}`}>
                <span className={`${chrome.statusItem} ${chrome.statusMuted}`}>
                  {String(pageInfo.badge)}
                </span>
              </Tooltip>
            </>
          )}
      </div>

      {(showRefresh || showSettings) && (
        <div className={chrome.statusActions} role="group" aria-label="Page actions">
          {showRefresh && (
            <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh page data'}>
              <button
                type="button"
                className={chrome.statusActionBtn}
                aria-label={refreshing ? 'Refreshing data' : 'Refresh page data'}
                aria-busy={refreshing || undefined}
                disabled={refreshing}
                onClick={handleRefresh}
              >
                <ReloadOutlined spin={refreshing} aria-hidden />
                {!isMobile && <span>Refresh</span>}
              </button>
            </Tooltip>
          )}
          {showSettings && (
            <Tooltip title="Page settings">
              <button
                type="button"
                className={chrome.statusActionBtnGhost}
                aria-label="Page settings"
                onClick={handleSettings}
              >
                <SettingOutlined aria-hidden />
              </button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}

export default PageInfoPanel
